import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPaperPlane,
  FaReply,
  FaTimes,
  FaUsers,
  FaRocket,
  FaShieldAlt,
} from "react-icons/fa";
import { FiLoader, FiSend, FiMessageCircle } from "react-icons/fi";
import { useChat } from "../../context/ChatContext";
import ChatMessage from "./ChatMessage";
import ChatSidebar from "./ChatSidebar";
import toast from "react-hot-toast";

const ChatWindow = ({ isExpanded }) => {
  const { user } = useSelector((state) => state.auth);
  const {
    messages,
    sendMessage,
    sendTyping,
    isLoading,
    isConnected,
    isMuted,
    isBanned,
    activeUsersCount,
    messagesEndRef,
    scrollToBottom,
    loadMessages,
  } = useChat();

  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const typingTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load messages when component mounts
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Auto focus input when chat opens
  useEffect(() => {
    if (inputRef.current && !isMuted && !isBanned) {
      inputRef.current.focus();
    }
  }, [isMuted, isBanned]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Handle typing indicator
  const handleTyping = useCallback(
    (value) => {
      setInputMessage(value);

      if (!isTyping && value.length > 0) {
        setIsTyping(true);
        sendTyping(true);
      }

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        if (isTyping) {
          setIsTyping(false);
          sendTyping(false);
        }
      }, 1500);
    },
    [isTyping, sendTyping],
  );

  const handleSendMessage = (e) => {
    e.preventDefault();

    if (isMuted) {
      toast.error("You are muted. Cannot send messages.");
      return;
    }

    if (isBanned) {
      toast.error("You are banned from chat.");
      return;
    }

    if (!inputMessage.trim()) {
      toast.error("Message cannot be empty");
      return;
    }

    if (inputMessage.length > 2000) {
      toast.error("Message too long (max 2000 characters)");
      return;
    }

    sendMessage(inputMessage, replyTo?._id);
    setInputMessage("");
    setReplyTo(null);
    sendTyping(false);
    inputRef.current?.focus();
  };

  const handleReply = (message) => {
    setReplyTo(message);
    inputRef.current?.focus();
  };

  const cancelReply = () => {
    setReplyTo(null);
  };

  const getMessageKey = (message, index) => {
    return `${message._id}-${message.createdAt}-${index}`;
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  if (isLoading) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          minHeight: "300px",
          background: getCardBackground(),
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <FiLoader size={30} color="#8b5cf6" />
        </motion.div>
        <p style={{ marginLeft: "10px", color: getTextSecondary() }}>
          Loading messages...
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        overflow: "hidden",
        height: "100%",
        position: "relative",
        background: getCardBackground(),
      }}
    >
      {/* Messages Area */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          width: showSidebar && !isMobile ? "calc(100% - 320px)" : "100%",
          transition: "width 0.3s ease",
          position: "relative",
        }}
      >
        {/* Messages Container */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: isMobile ? "12px" : "16px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {messages.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "3rem",
                color: getTextSecondary(),
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  background: "rgba(139,92,246,0.1)",
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1rem auto",
                }}
              >
                <FiMessageCircle size={30} color="#8b5cf6" />
              </div>
              <p>No messages yet. Be the first to say hello! 👋</p>
            </div>
          ) : (
            messages.map((message, index) => (
              <ChatMessage
                key={getMessageKey(message, index)}
                message={message}
                onReply={handleReply}
                isCurrentUser={
                  message.sender?._id === user?.id ||
                  message.sender?._id === user?._id
                }
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply Indicator */}
        <AnimatePresence>
          {replyTo && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              style={{
                padding: "10px 16px",
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderTop: `1px solid ${getBorderColor()}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  flex: 1,
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    background: "rgba(139,92,246,0.1)",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaReply size={14} color="#8b5cf6" />
                </div>
                <div style={{ flex: 1 }}>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#8b5cf6",
                      fontWeight: "600",
                    }}
                  >
                    Replying to {replyTo.senderName}
                  </span>
                  <p
                    style={{
                      fontSize: "11px",
                      color: getTextSecondary(),
                      marginTop: "2px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {replyTo.message.substring(0, 60)}
                  </p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={cancelReply}
                style={{
                  background: isDarkMode
                    ? "rgba(139,92,246,0.1)"
                    : "rgba(139,92,246,0.05)",
                  border: "none",
                  cursor: "pointer",
                  color: getTextSecondary(),
                  padding: "6px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaTimes size={12} />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input Area */}
        <form
          onSubmit={handleSendMessage}
          style={{
            padding: isMobile ? "12px 16px" : "16px 20px",
            borderTop: `1px solid ${getBorderColor()}`,
            display: "flex",
            gap: "12px",
            alignItems: "center",
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={inputMessage}
            onChange={(e) => handleTyping(e.target.value)}
            placeholder={
              isMuted
                ? "You are muted"
                : isBanned
                  ? "You are banned"
                  : "Type your message..."
            }
            disabled={isMuted || isBanned}
            style={{
              flex: 1,
              padding: isMobile ? "10px 16px" : "12px 20px",
              border: `2px solid ${getBorderColor()}`,
              borderRadius: "40px",
              outline: "none",
              fontSize: isMobile ? "13px" : "14px",
              background: getInputBackground(),
              color: getTextColor(),
              transition: "all 0.3s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
            onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
          />
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            disabled={!inputMessage.trim() || isMuted || isBanned}
            style={{
              background:
                !inputMessage.trim() || isMuted || isBanned
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              border: "none",
              borderRadius: "50%",
              width: isMobile ? "44px" : "48px",
              height: isMobile ? "44px" : "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor:
                !inputMessage.trim() || isMuted || isBanned
                  ? "not-allowed"
                  : "pointer",
              transition: "all 0.3s",
              boxShadow:
                !inputMessage.trim() || isMuted || isBanned
                  ? "none"
                  : "0 4px 14px 0 rgba(139,92,246,0.4)",
            }}
          >
            <FiSend size={isMobile ? 16 : 18} color="white" />
          </motion.button>
        </form>

        {/* Connection Status */}
        {!isConnected && (
          <div
            style={{
              padding: "8px",
              textAlign: "center",
              background: "#ef4444",
              color: "white",
              fontSize: "12px",
            }}
          >
            Reconnecting to chat server...
          </div>
        )}
      </div>

      {/* Sidebar Toggle Button - Desktop */}
      {!isMobile && !showSidebar && (
        <motion.button
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setShowSidebar(true)}
          style={{
            position: "absolute",
            right: "16px",
            top: "16px",
            background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            border: "none",
            borderRadius: "30px",
            padding: "6px 12px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "11px",
            zIndex: 10,
            color: "white",
            fontWeight: "500",
            boxShadow: "0 2px 8px rgba(139,92,246,0.3)",
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaUsers size={12} />
          {activeUsersCount} online
        </motion.button>
      )}

      {/* Sidebar Toggle Button - Mobile */}
      {isMobile && !showSidebar && (
        <motion.button
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={() => setShowSidebar(true)}
          style={{
            position: "absolute",
            right: "12px",
            top: "12px",
            background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            border: "none",
            borderRadius: "30px",
            padding: "5px 10px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "10px",
            color: "white",
            zIndex: 10,
            fontWeight: "500",
          }}
        >
          <FaUsers size={10} /> {activeUsersCount}
        </motion.button>
      )}

      {/* Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: isMobile ? "100%" : "320px", opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3, type: "spring", damping: 30 }}
            style={{
              borderLeft: `1px solid ${getBorderColor()}`,
              overflow: "hidden",
              position: isMobile ? "fixed" : "relative",
              top: isMobile ? 0 : "auto",
              right: isMobile ? 0 : "auto",
              bottom: isMobile ? 0 : "auto",
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              zIndex: isMobile ? 100 : "auto",
              height: isMobile ? "100%" : "auto",
              boxShadow: isMobile ? "-5px 0 25px rgba(0,0,0,0.2)" : "none",
            }}
          >
            <ChatSidebar
              onClose={() => setShowSidebar(false)}
              isMobile={isMobile}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.2); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
    </div>
  );
};

export default ChatWindow;
